import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeShiftChanges } from '../lib/realtime';

export interface StintRow {
  id: string;
  event: 'login' | 'logout';
  occurred_at: string;
}

/**
 * Raw login/logout stints for the last N days — the same source the mobile
 * app's körpass clock runs on. Live via realtime; the Körpass tab derives
 * per-day stint pairs and utloggning windows from this.
 */
export function useStintHistory(driverId: string | undefined, days = 31) {
  const [stints, setStints] = useState<StintRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!driverId) return;
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('session_stints')
      .select('id, event, occurred_at')
      .eq('driver_id', driverId)
      .gte('occurred_at', since)
      .order('occurred_at', { ascending: true });
    setStints((data ?? []) as StintRow[]);
    setLoading(false);
  }, [driverId, days]);

  useEffect(() => {
    if (!driverId) { setLoading(false); return; }
    refetch();
    const timer = setInterval(refetch, 30000);
    const unsub = subscribeShiftChanges(driverId, refetch);
    return () => {
      clearInterval(timer);
      unsub();
    };
  }, [driverId, refetch]);

  return { stints, loading };
}
